'use client';

import {
  useEffect,
  useLayoutEffect,
  useRef,
  useState,
  type CSSProperties,
  type ReactNode,
} from 'react';
import { createPortal } from 'react-dom';
import { cn } from '@/lib/cn';

interface PopoverProps {
  /** Viewport rect of the element the popover anchors to. */
  anchor: { top: number; left: number; width: number; height: number } | null;
  onClose: () => void;
  /** Panel width in px (default 320); clamped to the viewport. */
  width?: number;
  className?: string;
  children: ReactNode;
}

const MARGIN = 8;

/** The width the panel will actually be given, once clamped to the viewport. */
const clampWidth = (preferred: number) =>
  typeof window === 'undefined' ? preferred : Math.min(preferred, window.innerWidth - MARGIN * 2);

/**
 * Portaled anchored popover for rich hover/click details (calendar items,
 * previews). Positions beside the anchor, clamped to the viewport from its
 * measured height, closes on outside click / Escape / outside scroll / resize.
 */
export function Popover({ anchor, onClose, width: preferredWidth = 320, className, children }: PopoverProps) {
  const panelRef = useRef<HTMLDivElement>(null);
  const [style, setStyle] = useState<CSSProperties | null>(null);

  // Positions from the panel's MEASURED height (the pre-style render pass is
  // hidden, sized only for measuring): a fixed height estimate let tall panels
  // run past the bottom of the screen on end-of-day chips (#401). `children` is
  // a dep so list ⇄ item drill-downs on the same anchor re-clamp.
  useLayoutEffect(() => {
    if (!anchor) {
      setStyle(null);
      return;
    }
    const panel = panelRef.current;
    if (!panel) return;
    const width = clampWidth(preferredWidth);
    const maxHeight = window.innerHeight - MARGIN * 2;
    const height = Math.min(panel.offsetHeight, maxHeight);
    const rightSpace = window.innerWidth - (anchor.left + anchor.width);
    const left =
      rightSpace > width + 16
        ? anchor.left + anchor.width + 8
        : Math.max(MARGIN, anchor.left - width - 8);
    // Align with the anchor's top, pushed up just enough to keep the whole
    // panel on-screen; maxHeight + scroll covers panels taller than the viewport.
    const top = Math.max(MARGIN, Math.min(anchor.top, window.innerHeight - height - MARGIN));
    setStyle({ position: 'fixed', width, left, top, maxHeight, overflowY: 'auto' });
  }, [anchor, children, preferredWidth]);

  useEffect(() => {
    if (!anchor) return;
    const onDown = (e: MouseEvent) => {
      if (panelRef.current && !panelRef.current.contains(e.target as Node)) onClose();
    };
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose();
    };
    // The capture-phase listener below also sees scrolls INSIDE the panel (its
    // own overflow, or a scrolling list in `children`) — those must not dismiss;
    // only the page/pane behind the popover moving may, or the panel would close
    // the moment a long list is wheeled or its scrollbar dragged.
    const onScroll = (e: Event) => {
      if (panelRef.current?.contains(e.target as Node)) return;
      onClose();
    };
    const onResize = () => onClose();
    document.addEventListener('mousedown', onDown);
    document.addEventListener('keydown', onKey);
    // Capture: the anchor usually sits in a scrolling pane, not the window, and
    // a scroll event on that pane does not bubble.
    window.addEventListener('scroll', onScroll, true);
    window.addEventListener('resize', onResize);
    return () => {
      document.removeEventListener('mousedown', onDown);
      document.removeEventListener('keydown', onKey);
      window.removeEventListener('scroll', onScroll, true);
      window.removeEventListener('resize', onResize);
    };
  }, [anchor, onClose]);

  if (!anchor) return null;

  return createPortal(
    <div
      ref={panelRef}
      // The style-less pass exists only to be measured; it never paints —
      // useLayoutEffect swaps in the real position before the browser draws.
      // It measures at the CLAMPED width: at `preferredWidth` a panel wider
      // than the viewport wraps to fewer lines than it really will, under-
      // reporting its height, and the clamp then seats it too low.
      style={style ?? { position: 'fixed', top: 0, left: 0, width: clampWidth(preferredWidth), visibility: 'hidden' }}
      className={cn(
        'corner-brand-sm animate-fade-in thin-scroll z-50 border border-zinc-700 bg-zinc-900 shadow-xl shadow-black/40',
        className
      )}
    >
      {children}
    </div>,
    document.body
  );
}
